import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import es from "./locales/es.json";
import en from "./locales/en.json";

type Locale = "es" | "en";
type Dict = Record<string, string>;

const DICTS: Record<Locale, Dict> = {
  es: es as Dict,
  en: en as Dict,
};

interface I18nValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string, fallback?: string) => string;
}

const I18nContext = createContext<I18nValue | undefined>(undefined);
const STORAGE_KEY = "aeq-locale";

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const stored = typeof window !== "undefined" ? (localStorage.getItem(STORAGE_KEY) as Locale | null) : null;
    return stored === "en" || stored === "es" ? stored : "es";
  });

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    localStorage.setItem(STORAGE_KEY, l);
  }, []);

  const t = useCallback(
    (key: string, fallback?: string) => DICTS[locale][key] ?? fallback ?? key,
    [locale],
  );

  const value = useMemo<I18nValue>(() => ({ locale, setLocale, t }), [locale, setLocale, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useT() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useT must be used within <I18nProvider>");
  return ctx;
}
